import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const ChipTypeMap = {
    static: 'getStaticFragment',
    recommend: 'getRecommendFragment',
    struct: 'getStructuredFragment',
    ad: 'getAd',
    selectedPool: 'getSelectedPool',
    dynamic: 'getDynamicFragment'
};

// 碎片
export interface TYPE_Chip {
    id: string // 碎片id，如果 type 为 api，id 就是接口的 url
    title: string // 碎片的中文名称
    type: 'recommend' | 'static' | 'struct' | 'ad' | 'selectedPool' | 'dynamic' | 'api' // 碎片类型
    // 【分割线上面的要明确写在 meta.ts 里】
    // ----------------分割线-------------------
    // 【分割线下面的可选，但是如果有值，就要写在 meta.ts 里】
    format?: {
        filter?: Array<string> // 筛选字段，只获取需要的，对于推荐位碎片来说，默认值：["thumbnails","title","type","url","base62Id","summary", "children", "position"]
    }
    default?: {
        type: 'recommend' | 'static' | 'struct' | 'ad' | 'selectedPool' | 'dynamic' | 'api'
        id: string
    }
    children?: TYPE_Chip // 用于打底数据，外层是打底的，children 是干涉的
    // ----------------分割线-------------------
    uuid?: string // 碎片的唯一标识，id 不为空时有值，id 每次变更时重新生成
    force?: string // 编辑碎片之后的强制更新
    html?: string // 给静态碎片用的，自定义html代码
}

/**
 * 获取碎片信息
 * 请求之后，会数据存放在全局变量里。再次请求之前，从全局变量里查询有没有已有数据，有就直接用，没有就再次请求，这样可以很明显地节省时间。
 *
 * @param {TYPE_Chip} chip 碎片信息
 *
 * @return 请求到碎片信息
 *
 * */
export const useChip = (chip: TYPE_Chip) => {
    const {
        id: chipId,
        type: chipType,
        force: forceUpdate,
        html,
        uuid,
        children
    } = chip || { id: '', type: '', force: '', html: '', uuid: '', children: { id: '', type: 'recommend', title: '默认干涉数据' } };

    const { type: defaultType, id: defaultId } = chip.default || { type: 'recommend', id: '' }

    let { filter } = chip.format || { filter: [] };
    if (filter.length < 1 && chipType === 'recommend') {
        filter = ['thumbnails', 'title', 'type', 'url', 'base62Id', 'summary', 'children', 'position'];
    }

    const [chipData, setChipData] = useState(null);
    const [ childrenData, setChildrenData ] = useState([])
    const oldForceUpdate = useRef(forceUpdate);

    useEffect(() => {
        if (forceUpdate === oldForceUpdate.current) {
            fetchChipData();
        } else {
            oldForceUpdate.current = forceUpdate;
            fetchChipData(true);
        }
    }, [chipId, chipType, forceUpdate]);

    useEffect(() => {
        fetchRecommendData()
    }, [children])

    const fetchChipData = async (forceUpdate = false) => {
        const id = chipId || defaultId;
        const type = chipId ? chipType : defaultType

        if (!forceUpdate && window.allData && window.allData[`${type}_${id}_${uuid}`]) {
            setChipData(window.allData[`${type}_${id}_${uuid}`]);
        } else {
            if (chipType === 'api') {
                if (chipId) {
                    const res = await axios.get(chipId)
                    if (!window.allData) {
                        window.allData = {};
                    }
                    window.allData[`${chipType}_${id}_${uuid}`] = res.data || [];
                    setChipData(res.data || []);
                } else {
                    setChipData([]);
                    return
                }
            } else {
                const res = await axios.post(
                    'https://ucms.ifeng.com/platform/lowcode/api/custom/dev/data',
                    {
                        transferV3: [
                            [
                                'data',
                                'KVProxy',
                                ChipTypeMap[type],
                                id,
                                filter.length > 0 ? { filter } : {},
                            ],
                        ],
                    },
                );
                if (!window.allData) {
                    window.allData = {};
                }
                if (chipType === 'ad' && res.data.allData.data) {
                    res.data.allData.data = new Function(`return ${res.data.allData.data}`);
                }
                window.allData[`${chipType}_${id}_${uuid}`] = res.data.allData.data || [];
                setChipData(res.data.allData.data || []);
            }
        }
    };

    const fetchRecommendData = async () => {
        if (!children) {
            setChildrenData([])
            return
        }

        const { id: chipId, type: chipType } = children
        const res = await axios.post(
            'https://ucms.ifeng.com/platform/lowcode/api/custom/dev/data',
            {
                transferV3: [
                    [
                        'childrenData',
                        'KVProxy',
                        ChipTypeMap[chipType],
                        chipId,
                        filter.length > 0 ? { filter } : {},
                    ],
                ],
            },
        );

        setChildrenData(res.data.allData.childrenData || [])
    };

    if (chipType === 'static' && !chipId && html) {
        return html;
    }

    if (childrenData.length > 0) { // 有打底数据的情况下，打底和干涉都是数组
        const totalLength = childrenData.length + (chipData || []).length
        const arr = new Array(totalLength)

        childrenData.forEach(c => {
            if (c.position !== undefined) {
                fillArray(arr, Number(c.position), c)
            }
        })

        childrenData.filter(c => c.position === undefined).concat(chipData).forEach((chip, index) => {
            fillArray(arr, index, chip)
        })

        return arr
    }

    return chipData || [];
};

const fillArray = (array, index, data) => {
    if (index < array.length) {
        if (array[index]) {
            fillArray(array, index + 1, data)
        } else {
            array[index] = data
        }
    } else {
        fillArray(array, 0, data)
    }
}
